import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
export default function PointSmartChargingWizard({
    onSubmit,
    chargingPointPublicId,
    initialProfile
}) {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [mode, setMode] = useState("none");

    const [allDayPower, setAllDayPower] = useState("");
    const [chargerMaxPower, setChargerMaxPower] = useState("");

    const [periods, setPeriods] = useState([{ start: "", end: "", power: "" }]);
    const [isSubmitting, setIsSubmitting] = useState(false);


    const steps =
        mode === "allday"
            ? ["Date", "Mode", "Power", "Summary"]
            : ["Date", "Mode", "Max power", "Details", "Summary"];
    const stepLabel = {
        Date: t("common.date"),
        Mode: t("smartCharging.mode"),
        Power: t("common.power"),
        Summary: t("common.summary"),
        "Max power": t("smartCharging.maxPower"),
        Details: t("common.details"),
    };

    const nextStep = () => {
        // Skip periods-step when allday
        if (mode === "allday" && step === 2) {
            setStep(steps.length - 1);
            return;
        }
        setStep((s) => s + 1);
    };

    const prevStep = () => setStep((s) => s - 1);



    const hasDates = startDate && endDate;
    const dateOrderInvalid = hasDates && new Date(startDate) > new Date(endDate);

    const arePeriodsComplete = periods.every((p) => p.start && p.end && p.power);

    const hasInvalidPeriod = periods.some((p) => {
        if (!p.start || !p.end) return false;
        return timeToSeconds(p.end) <= timeToSeconds(p.start);
    });

    const hasOverlap = checkOverlap(periods);

    const canGoNextFromPeriods =
        arePeriodsComplete && !hasInvalidPeriod && !hasOverlap;


    useEffect(() => {
        if (!initialProfile) {
            setStartDate("");
            setEndDate("");
            setMode("none");
            setAllDayPower("");
            setChargerMaxPower("");
            setPeriods([{ start: "", end: "", power: "" }]);
            setStep(0);
            return;
        }

        setStartDate(extractDate(initialProfile.startDate));
        setEndDate(extractDate(initialProfile.endDate));

        setMode(initialProfile.mode ?? "none");

        if (initialProfile.mode === "allday") {
            setAllDayPower(initialProfile.allDayPower ?? "");
            setChargerMaxPower(initialProfile.chargerMaxPower ?? "");
            setPeriods([{ start: "", end: "", power: "" }]); // not used
        } else {
            setChargerMaxPower(initialProfile.chargerMaxPower ?? "");
            setPeriods(
                initialProfile.periods?.map((p) => ({
                    start: p.start ?? "",
                    end: p.end ?? "",
                    power: p.power ?? ""
                })) ?? [{ start: "", end: "", power: "" }]
            );
        }

        setStep(0); 
    }, [initialProfile]);

    function extractDate(value) {
        if (!value) return "";
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
            return d.toISOString().split("T")[0];
        }
        return value.split("T")[0];
    }



    const addPeriod = () =>
        setPeriods([...periods, { start: "", end: "", power: "" }]);

    const updatePeriod = (index, field, value) => {
        const updated = [...periods];
        updated[index][field] = value;
        setPeriods(updated);
    };

    const removePeriod = (index) =>
        setPeriods(periods.filter((_, i) => i !== index));

 
    const handleSubmit = () => {
        if (isSubmitting) return;       // Blockera dubbelklick
        setIsSubmitting(true);

        const result = {
            mode,
            startDate,
            endDate,
            allDayPower,
            chargerMaxPower,
            periods: mode === "periods" ? periods : [], 
            chargingPointPublicId
        };
        if (initialProfile?.existingId) {
            result.existingId = initialProfile.existingId;
            result.existingChargingProfilePublicId = initialProfile.existingChargingProfilePublicId;
            result.existingStackLevel = initialProfile.existingStackLevel;
        }

        onSubmit(result);

    };


    return (
        <div className="w-full p-2 sm:p-4 md:p-6 space-y-8">
            {/* Step indicators */}
            <div className="flex items-center justify-between mb-4">
                {steps.map((label, i) => (
                    <div key={label} className="flex-1 text-center">
                        <div
                            className={
                                "w-8 h-8 mx-auto rounded-full flex items-center justify-center font-semibold " +
                                (step === i
                                    ? "bg-blue-500 text-white"
                                    : "bg-mute-200 text-mute-600")
                            }
                        >
                            {i + 1}
                        </div>
                        <div className="mt-1 text-xs font-semibold text-mute-700">
                            {stepLabel[label] ?? label}
                        </div>

                    </div>

                ))}

            </div>
            <hr className="border-mute-300" />

            {/* STEP 0 – Date */}
            {step === 0 && (
                <div className="space-y-6">

                    <h2 className="text-xl font-bold ">{t("smartCharging.validityPeriod")}</h2>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-mute-700">{t("form.startDate")}</label>
                            <input
                                type="date"
                                className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-mute-700">{t("form.endDate")}</label>
                            <input
                                type="date"
                                className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                    </div>

                    {dateOrderInvalid && (
                        <p className="text-red-600 text-sm">
                            {t("smartCharging.endDateAfterStart")}
                        </p>
                    )}

                    <div className="flex justify-end">
                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50 "
                            disabled={!hasDates || dateOrderInvalid}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 1 – Mode */}
            {step === 1 && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">
                        {t("smartCharging.whenApply")}
                    </h2>

                    <label className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-mute-50">
                        <input
                            type="radio"
                            value="allday"
                            checked={mode === "allday"}
                            onChange={() => setMode("allday")}
                        />
                        <div className="font-medium text-mute-800">{t("smartCharging.allDay247")}</div>
                    </label>

                    <label className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-mute-50">
                        <input
                            type="radio"
                            value="periods"
                            checked={mode === "periods"}
                            onChange={() => setMode("periods")}
                        />
                        <div className="font-medium text-mute-800">{t("smartCharging.specificPeriods")}</div>
                    </label>

                    <div className="flex justify-between mt-6">
                        <button
                            className="btn btn-outline-primary px-4 py-2"
                            onClick={prevStep}
                        >
                            {t("common.back")}
                        </button>

                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50 "
                            disabled={mode === "none"}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}

            {step === 2 && mode === "allday" && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("smartCharging.allDayPower")}</h2>

                    <label className="text-sm font-medium text-mute-700">{t("form.powerKw")}</label>
                    <input
                        type="number"
                        className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                        value={allDayPower}
                        onChange={(e) => setAllDayPower(e.target.value)}
                    />

                    <div className="flex justify-between mt-6">
                        <button
                            className="btn btn-outline-primary px-4 py-2"
                            onClick={prevStep}
                        >
                            {t("common.back")}
                        </button>

                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50 "
                            disabled={!allDayPower}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}
            {step === 2 && mode === "periods" && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("smartCharging.maxChargingPower")}</h2>

                    <label className="text-sm font-medium text-mute-700">{t("form.maxPowerKw")}</label>
                    <input
                        type="number"
                        className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                        value={chargerMaxPower}
                        onChange={(e) => setChargerMaxPower(e.target.value)}
                    />

                    <p className="text-xs text-mute-500">
                        {t("smartCharging.maxPowerBetween")}
                    </p>

                    <div className="flex justify-between mt-6">
                        <button
                            className="btn btn-outline-primary px-4 py-2"
                            onClick={prevStep}
                        >
                            {t("common.back")}
                        </button>

                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50"
                            disabled={!chargerMaxPower}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}
            {step === 3 && mode === "periods" && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("form.timePeriods")}</h2>

                    <div className="space-y-4">
                        {periods.map((p, index) => (
                            <div key={index} className="p-4 border rounded-xl space-y-3 bg-mute-50">

                                <div className="grid grid-cols-7 gap-1 items-end">

                                    {/* Start time */}
                                    <div className="col-span-2">
                                        <label className="text-sm font-medium text-mute-700">{t("form.startTime")}</label>
                                        <input
                                            type="time"
                                            className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                            value={p.start}
                                            onChange={(e) => updatePeriod(index, "start", e.target.value)}
                                        />
                                    </div>

                                    {/* End time */}
                                    <div className="col-span-2">
                                        <label className="text-sm font-medium text-mute-700">{t("form.endTime")}</label>
                                        <input
                                            type="time"
                                            className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                            value={p.end}
                                            onChange={(e) => updatePeriod(index, "end", e.target.value)}
                                        />
                                    </div>

                                    {/* Power */}
                                    <div className="col-span-2">
                                        <label className="text-sm font-medium text-mute-700">{t("form.powerKw")}</label>
                                        <input
                                            type="number"
                                            className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                            value={p.power}
                                            onChange={(e) => updatePeriod(index, "power", e.target.value)}
                                        />
                                    </div>

                                    {/* Delete icon */}
                                    <div className="flex justify-center col-span-1">
                                        {periods.length > 1 && (
                                            <button
                                                className="text-red-600 hover:text-red-800"
                                                onClick={() => removePeriod(index)}
                                                title={t("common.removePeriod")}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        )}
                                    </div>

                                </div>

                            </div>
                        ))}
                    </div>


                    {/* ADD PERIOD BUTTON */}
                    <button
                        className="px-4 py-2 btn btn-secondary rounded-lg"
                        onClick={addPeriod}
                    >
                        {t("common.addPeriod")}
                    </button>

                    {/* Validation messages */}
                    {hasInvalidPeriod && (
                        <p className="text-red-600 text-sm">{t("smartCharging.endTimeAfterStart")}</p>
                    )}
                    {arePeriodsComplete && !hasInvalidPeriod && hasOverlap && (
                        <p className="text-red-600 text-sm">{t("smartCharging.periodsOverlap")}</p>
                    )}

                    <div className="flex justify-between mt-6">
                        <button
                            className="btn btn-outline-primary px-4 py-2"
                            onClick={prevStep}
                        >
                            {t("common.back")}
                        </button>

                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50"
                            disabled={!canGoNextFromPeriods}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}


{/* STEP 4 – Summary */ }
{
    step === steps.length - 1 && (
        <div className="space-y-4">
            <h2 className="text-xl font-bold ">{t("common.summary")}</h2>

            <div className="bg-mute-50 p-4 rounded-xl text-sm text-mute-800">
                <p><strong>{t("common.date")}:</strong> {startDate} – {endDate}</p>
            </div>

            {mode === "allday" && (
                <div className="bg-mute-50 p-4 rounded-xl text-sm text-mute-800 space-y-1">
                    <p><strong>{t("smartCharging.mode")}:</strong> {t("smartCharging.allDay247")}</p>
                    <p><strong>{t("common.power")}:</strong> {allDayPower} kW</p>
                </div>
            )}

            {mode === "periods" && (
                <>
                    <div className="bg-mute-50 p-4 rounded-xl text-sm text-mute-800">
                        <p><strong>{t("smartCharging.maxPower")}:</strong> {chargerMaxPower} kW</p>
                    </div>

                    <div className="space-y-2">
                        {periods
                            .slice()
                            .sort((a, b) => timeToSeconds(a.start) - timeToSeconds(b.start))
                            .map((p, i) => (
                                <div key={i} className="bg-mute-50 p-4 rounded-xl text-sm text-mute-800">
                                    <p><strong>{t("common.period", { n: i + 1 })}:</strong> {p.start} – {p.end}</p>
                                    <p><strong>{t("common.power")}:</strong> {p.power} kW</p>
                                </div>
                            ))}
                    </div>
                </>
            )}
                        <div className="flex justify-between mt-6">
                            <button
                                className="btn btn-outline-primary px-4 py-2"
                                onClick={prevStep}
                            >
                                {t("common.back")}
                            </button>

                            <button
                                className="btn btn-primary px-4 py-2 bg-blue-600 text-white rounded-lg"
                                onClick={handleSubmit}
                            >
                                {initialProfile ? t("common.saveChanges") : t("common.createProfile")}
                            </button>
                        </div>
        </div>
    )
}
    </div >
);
}


function timeToSeconds(t) {
    const [h, m] = t.split(":").map(Number);
    return h * 3600 + m * 60;
}

function checkOverlap(periods) {
    const mapped = periods
        .filter((p) => p.start && p.end)
        .map((p) => ({
            start: timeToSeconds(p.start),
            end: timeToSeconds(p.end)
        }))
        .sort((a, b) => a.start - b.start);

    for (let i = 1; i < mapped.length; i++) {
        if (mapped[i].start < mapped[i - 1].end) return true;
    }
    return false;
}
